import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { EnvironmentService } from '../environment/environment.service';

/**
 * Generic metric log request structure
 */
export interface MetricLogRequest {
  EventCode: string;
  [key: string]: string | unknown;
}

/**
 * Generic service for logging metrics to the backend API.
 * This is a shared service that can be used by different feature modules
 * to send analytics/telemetry data.
 *
 * Usage:
 * ```typescript
 * this.metricLogger.logMetric({
 *   EventCode: 'MyEventCode',
 *   SomeField: 'someValue',
 *   DeviceType: 'Mobile'
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class MetricLoggerService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly environmentService: EnvironmentService
  ) {}

  /**
   * Send a metric event to the backend API.
   *
   * @param metricData The metric data to log (must include EventCode)
   * @returns Observable<boolean> - true if successful, false if failed
   *
   * Note: Errors are silently caught and logged to console to avoid
   * blocking the user experience with analytics failures.
   */
  logMetric(metricData: MetricLogRequest): Observable<boolean> {
    const baseUrl = this.environmentService.appointmentSchedulingBaseUrl;
    const metricURL = `${baseUrl}metric`;

    return this.httpClient
      .post<unknown>(metricURL, metricData, {
        headers: { 'x-client-type': 'patient' },
      })
      .pipe(
        map(() => true),
        catchError((err: HttpErrorResponse) => {
          console.error('Failed to log metric:', err, metricData);
          // Silently ignore errors - analytics should never block user experience
          return of(false);
        })
      );
  }
}
